/**
 * DIISA Keramik - Theme JavaScript
 * Production-quality implementation with:
 * - AJAX Cart API (add, update, remove)
 * - Cart drawer with dynamic updates
 * - Header scroll effect
 * - Mobile menu drawer
 * - Smooth scroll for anchor links
 * - Keyboard accessibility (Escape to close)
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY: Fetch wrapper for Shopify Cart API
  // ============================================
  const CartAPI = {
    async getCart() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return response.json();
    },

    async addItem(variantId, quantity = 1) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: quantity }]
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Could not add item to cart');
      }
      return response.json();
    },

    async updateItem(key, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      return response.json();
    },

    async removeItem(key) {
      return this.updateItem(key, 0);
    }
  };

  // ============================================
  // HEADER SCROLL EFFECT
  // ============================================
  const header = document.querySelector('[data-header]');

  if (header) {
    window.addEventListener('scroll', function() {
      if (window.scrollY > 50) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }, { passive: true });
  }

  // ============================================
  // MOBILE MENU
  // ============================================
  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileMenuOverlay = document.querySelector('[data-mobile-menu-overlay]');
  const mobileMenuDrawer = document.querySelector('[data-mobile-menu-drawer]');
  const mobileMenuClose = document.querySelector('[data-mobile-menu-close]');

  function openMobileMenu() {
    if (mobileMenuOverlay && mobileMenuDrawer) {
      mobileMenuOverlay.classList.add('is-open');
      mobileMenuDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus trap: focus the close button
      if (mobileMenuClose) mobileMenuClose.focus();
    }
  }

  function closeMobileMenu() {
    if (mobileMenuOverlay && mobileMenuDrawer) {
      mobileMenuOverlay.classList.remove('is-open');
      mobileMenuDrawer.classList.remove('is-open');
      document.body.style.overflow = '';
      // Return focus to toggle button
      if (mobileMenuToggle) mobileMenuToggle.focus();
    }
  }

  if (mobileMenuToggle) {
    mobileMenuToggle.addEventListener('click', openMobileMenu);
  }

  if (mobileMenuClose) {
    mobileMenuClose.addEventListener('click', closeMobileMenu);
  }

  if (mobileMenuOverlay) {
    mobileMenuOverlay.addEventListener('click', closeMobileMenu);
  }

  // Mobile menu dropdowns
  document.querySelectorAll('[data-mobile-dropdown-toggle]').forEach(function(toggle) {
    toggle.addEventListener('click', function() {
      var dropdown = this.nextElementSibling;

      if (dropdown && dropdown.hasAttribute('data-mobile-dropdown')) {
        var isOpen = dropdown.classList.contains('is-open');

        // Close all dropdowns first
        document.querySelectorAll('[data-mobile-dropdown]').forEach(function(d) {
          d.classList.remove('is-open');
        });
        document.querySelectorAll('[data-mobile-dropdown-toggle]').forEach(function(t) {
          t.classList.remove('is-open');
        });

        // Toggle current
        if (!isOpen) {
          dropdown.classList.add('is-open');
          this.classList.add('is-open');
        }
      }
    });
  });

  // ============================================
  // CART DRAWER
  // ============================================
  const cartToggle = document.querySelector('[data-cart-toggle]');
  const cartOverlay = document.querySelector('[data-cart-overlay]');
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartCloseButtons = document.querySelectorAll('[data-cart-close]');

  function openCartDrawer() {
    if (cartOverlay && cartDrawer) {
      cartOverlay.classList.add('is-open');
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus the close button for accessibility
      var closeBtn = cartDrawer.querySelector('[data-cart-close]');
      if (closeBtn) closeBtn.focus();
    }
  }

  function closeCartDrawer() {
    if (cartOverlay && cartDrawer) {
      cartOverlay.classList.remove('is-open');
      cartDrawer.classList.remove('is-open');
      document.body.style.overflow = '';
      // Return focus to cart toggle
      if (cartToggle) cartToggle.focus();
    }
  }

  if (cartToggle) {
    cartToggle.addEventListener('click', openCartDrawer);
  }

  if (cartOverlay) {
    cartOverlay.addEventListener('click', closeCartDrawer);
  }

  cartCloseButtons.forEach(function(btn) {
    btn.addEventListener('click', closeCartDrawer);
  });

  // ============================================
  // CART: Update badge count
  // ============================================
  function updateCartCount(count) {
    var badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach(function(badge) {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = 'flex';
      } else {
        badge.style.display = 'none';
      }
    });
  }

  // ============================================
  // CART: Refresh drawer content via page reload
  // (For a full SPA-like experience, you'd render
  //  the cart HTML client-side. This approach
  //  refreshes the section for simplicity.)
  // ============================================
  async function refreshCartDrawer() {
    try {
      // Fetch the cart page to get updated drawer HTML
      var response = await fetch('/?sections=footer-group');
      // Since we can't easily fetch just the cart drawer snippet,
      // we'll do a simpler approach: reload the page section
      // For now, just update the count and total
      var cart = await CartAPI.getCart();
      updateCartCount(cart.item_count);

      // Update subtotal if visible
      var totalEl = document.querySelector('[data-cart-total]');
      if (totalEl) {
        // Format price (Shopify prices are in cents)
        var formatted = (cart.total_price / 100).toLocaleString('da-DK', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        totalEl.textContent = formatted + ' DKK';
      }

      // If cart is now empty, show empty state
      if (cart.item_count === 0) {
        var cartBody = document.querySelector('[data-cart-body]');
        var cartFooter = document.querySelector('[data-cart-footer]');
        if (cartBody) {
          cartBody.innerHTML = '<div class="cart-drawer__empty">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
            '<p>Din kurv er tom</p>' +
            '<p>Tilføj nogle smukke keramikprodukter</p>' +
            '</div>';
        }
        if (cartFooter) {
          cartFooter.style.display = 'none';
        }
      }
    } catch (err) {
      console.error('Error refreshing cart:', err);
    }
  }

  // ============================================
  // CART: Add to cart (AJAX)
  // ============================================
  document.addEventListener('click', async function(e) {
    var addBtn = e.target.closest('[data-add-to-cart]');
    if (!addBtn) return;

    e.preventDefault();
    var variantId = addBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    // Visual feedback
    var originalText = addBtn.textContent;
    addBtn.disabled = true;
    addBtn.textContent = '...';

    try {
      await CartAPI.addItem(variantId, 1);
      addBtn.textContent = '✓';

      // Update cart count
      var cart = await CartAPI.getCart();
      updateCartCount(cart.item_count);

      // Open cart drawer
      openCartDrawer();

      // Reload page to update cart drawer contents
      // (In a more advanced setup, you'd render the cart items client-side)
      setTimeout(function() {
        window.location.reload();
      }, 300);
    } catch (err) {
      console.error('Add to cart error:', err);
      addBtn.textContent = 'Fejl';
      setTimeout(function() {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      }, 2000);
    }
  });

  // ============================================
  // CART: Quantity change (AJAX)
  // ============================================
  document.addEventListener('click', async function(e) {
    var qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (!qtyBtn) return;

    e.preventDefault();
    var key = qtyBtn.getAttribute('data-cart-qty-change');
    var newQty = parseInt(qtyBtn.getAttribute('data-qty'), 10);

    try {
      if (newQty <= 0) {
        await CartAPI.removeItem(key);
      } else {
        await CartAPI.updateItem(key, newQty);
      }
      // Reload to reflect changes
      window.location.reload();
    } catch (err) {
      console.error('Cart update error:', err);
    }
  });

  // ============================================
  // CART: Remove item (AJAX)
  // ============================================
  document.addEventListener('click', async function(e) {
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (!removeBtn) return;

    e.preventDefault();
    var key = removeBtn.getAttribute('data-cart-remove');

    // Visual feedback: fade out the item
    var cartItem = removeBtn.closest('[data-cart-item]');
    if (cartItem) {
      cartItem.style.opacity = '0.5';
      cartItem.style.pointerEvents = 'none';
    }

    try {
      await CartAPI.removeItem(key);
      window.location.reload();
    } catch (err) {
      console.error('Cart remove error:', err);
      if (cartItem) {
        cartItem.style.opacity = '1';
        cartItem.style.pointerEvents = '';
      }
    }
  });

  // ============================================
  // SEARCH OVERLAY
  // ============================================
  var searchToggle = document.querySelector('[data-search-toggle]');
  var searchOverlay = document.querySelector('[data-search-overlay]');
  var searchClose = document.querySelector('[data-search-close]');
  var searchInput = document.querySelector('[data-search-input]');
  var searchResults = document.querySelector('[data-search-results]');
  var searchDebounceTimer = null;

  function openSearch() {
    if (searchOverlay) {
      searchOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus the input after animation
      setTimeout(function() {
        if (searchInput) searchInput.focus();
      }, 200);
    }
  }

  function closeSearch() {
    if (searchOverlay) {
      searchOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (searchInput) searchInput.value = '';
      if (searchResults) searchResults.innerHTML = '';
      if (searchToggle) searchToggle.focus();
    }
  }

  if (searchToggle) {
    searchToggle.addEventListener('click', openSearch);
  }

  if (searchClose) {
    searchClose.addEventListener('click', closeSearch);
  }

  // Close on overlay click (not inner)
  if (searchOverlay) {
    searchOverlay.addEventListener('click', function(e) {
      if (e.target === searchOverlay) closeSearch();
    });
  }

  // Predictive search
  if (searchInput && searchResults) {
    searchInput.addEventListener('input', function() {
      var query = this.value.trim();

      clearTimeout(searchDebounceTimer);

      if (query.length < 2) {
        searchResults.innerHTML = '';
        return;
      }

      searchDebounceTimer = setTimeout(async function() {
        try {
          var response = await fetch(
            '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=5'
          );
          var data = await response.json();
          var products = data.resources && data.resources.results && data.resources.results.products
            ? data.resources.results.products
            : [];

          if (products.length === 0) {
            searchResults.innerHTML = '<p style="text-align: center; color: var(--color-text-light); font-size: 0.875rem; padding: 1rem 0;">Ingen resultater fundet</p>';
            return;
          }

          var html = '';
          products.forEach(function(product) {
            var imageUrl = product.image
              ? product.image.replace(/(\.[^.]+)$/, '_150x$1')
              : '';
            html += '<a href="' + product.url + '" class="search-result-item">';
            if (imageUrl) {
              html += '<img src="' + imageUrl + '" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" class="search-result-item__image" loading="lazy">';
            }
            html += '<div class="search-result-item__info">';
            html += '<p class="search-result-item__title">' + product.title + '</p>';
            html += '<p class="search-result-item__price">' + product.price + '</p>';
            html += '</div>';
            html += '</a>';
          });

          searchResults.innerHTML = html;
        } catch (err) {
          console.error('Search error:', err);
          searchResults.innerHTML = '';
        }
      }, 300);
    });
  }

  // ============================================
  // CLOSE DRAWERS ON ESCAPE KEY
  // ============================================
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      closeMobileMenu();
      closeCartDrawer();
      closeSearch();
    }
  });

  // ============================================
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // ============================================
  document.addEventListener('click', function(e) {
    var anchor = e.target.closest('a[href*="#"]');
    if (!anchor) return;

    var href = anchor.getAttribute('href');
    if (!href || href === '#') return;

    // Handle both "#section" and "/#section" formats
    var hashIndex = href.indexOf('#');
    if (hashIndex === -1) return;

    var hash = href.substring(hashIndex);
    var path = href.substring(0, hashIndex);

    // If the link points to a different page, let it navigate normally
    if (path && path !== '/' && path !== window.location.pathname) return;

    var target = document.querySelector(hash);
    if (target) {
      e.preventDefault();
      var headerHeight = header ? header.offsetHeight : 0;
      var targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      closeMobileMenu();
    }
  });

  // ============================================
  // FEATURED PRODUCT: Add to cart form
  // ============================================
  document.querySelectorAll('form[action="/cart/add"]').forEach(function(form) {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();

      var formData = new FormData(form);
      var variantId = formData.get('id');
      var quantity = parseInt(formData.get('quantity') || '1', 10);
      var submitBtn = form.querySelector('button[type="submit"]');

      if (!variantId || !submitBtn) return;

      var originalText = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = '...';

      try {
        await CartAPI.addItem(variantId, quantity);
        submitBtn.textContent = '✓ Tilføjet';

        var cart = await CartAPI.getCart();
        updateCartCount(cart.item_count);
        openCartDrawer();

        setTimeout(function() {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      } catch (err) {
        console.error('Add to cart error:', err);
        submitBtn.textContent = 'Fejl - prøv igen';
        setTimeout(function() {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  });

})();
